import React, { useState, useEffect } from "react";
import HPageContainer from "../../../hcomponents/HPageContainer";
import HHeader from "../../../hcomponents/HHeader";
import HActionButton from "../../../hcomponents/HActionButton";
import { Search, Bell, Calendar, CheckCircle, Trash2 } from "lucide-react";
import { Constants } from "../../../utils/constants";

// Shimmer Loading Component
const ShimmerLoading = () => {
  return (
    <div className="space-y-2">
      {[1, 2, 3, 4, 5].map(item => (
        <div
          key={item}
          className="bg-white rounded-xl shadow-sm p-3 border border-gray-100 w-full flex items-center animate-pulse"
        >
          <div className="w-16 h-16 bg-gray-200 rounded-md mr-3 flex-shrink-0"></div>
          <div className="flex-1">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-full mb-1"></div>
            <div className="h-3 bg-gray-200 rounded w-4/5"></div>
            <div className="flex items-center justify-between mt-2">
              <div className="h-3 bg-gray-200 rounded w-1/4"></div>
              <div className="h-3 bg-gray-200 rounded w-1/4"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const HostNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState(null);

  // Generate dummy data
  useEffect(() => {
    // Simulate API loading
    setTimeout(() => {
      const dummyNotifications = [
        {
          id: 1,
          title: "New Booking Request",
          message:
            "You have a new booking request for Workspace A. The user has requested a meeting room for 10 people on Friday.",
          createdAt: new Date(2023, 11, 16, 14, 30),
          isRead: false,
        },
        {
          id: 2,
          title: "Payment Confirmation",
          message:
            "Payment received for Invoice #1234. The transaction has been completed successfully and the funds have been transferred to your account.",
          createdAt: new Date(2023, 11, 15, 9, 45),
          isRead: true,
        },
        {
          id: 3,
          title: "Booking Canceled",
          message:
            "Booking for Conference Room B has been canceled by the user. The slot is now available for booking again.",
          createdAt: new Date(2023, 11, 14, 16, 20),
          isRead: false,
        },
        {
          id: 4,
          title: "Office Closure Notice",
          message:
            "Our offices will be closed on Monday for the holiday. Please make alternative arrangements if you have meetings scheduled.",
          createdAt: new Date(2023, 11, 15),
          isRead: false,
        },
        {
          id: 5,
          title: "New Workspace Launch",
          message:
            "We are excited to announce the launch of our new premium workspace in downtown. The new space features 20 private offices.",
          createdAt: new Date(2023, 11, 10),
          isRead: true,
        },
      ];

      setNotifications(dummyNotifications);
      setIsLoading(false);
    }, 1000);
  }, []);

  const formatDateTime = date => {
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const markAllAsRead = () => {
    setNotifications(prev =>
      prev.map(notification => ({
        ...notification,
        isRead: true,
      }))
    );
  };

  const markAsRead = id => {
    setNotifications(prev =>
      prev.map(notification => (notification.id === id ? { ...notification, isRead: true } : notification))
    );
  };

  const handleDeleteClick = notification => {
    setNotificationToDelete(notification);
    setShowConfirmDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (notificationToDelete) {
      setNotifications(notifications.filter(n => n.id !== notificationToDelete.id));
      setNotificationToDelete(null);
      setShowConfirmDeleteModal(false);
    }
  };

  const filteredNotifications = notifications.filter(
    notification =>
      notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notification.message.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <HPageContainer>
      <HHeader rightButton={<HActionButton label="Mark All as Read" variant="primary" onClick={markAllAsRead} />} />

      <div className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
        <div className="w-full">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4">
            <h1
              className="text-xl font-bold hidden sm:block"
              style={{
                color: Constants.HOST_EXPERIENCE_BASE_COLOR,
              }}
            >
              {/* Notifications */}
            </h1>
            {/* Search Bar */}
            <div className="relative w-full sm:w-64 ml-auto">
              <input
                type="text"
                placeholder="Search..."
                className="w-full p-2 pl-10 bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-2"
                style={{
                  "--tw-ring-color": `${Constants.HOST_EXPERIENCE_BASE_COLOR}50`,
                }}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              <Search className="absolute left-3.5 top-2.5 text-gray-400 w-4 h-4" />
            </div>
          </div>

          {/* Loading State - Shimmer Effect */}
          {isLoading ? (
            <ShimmerLoading />
          ) : (
            <>
              {/* Notifications List */}
              {filteredNotifications.length > 0 ? (
                <div className="space-y-2">
                  {filteredNotifications.map(notification => (
                    <div
                      key={notification.id}
                      className={`bg-white rounded-xl shadow-sm p-3 border border-gray-100 w-full flex items-center ${!notification.isRead ? "bg-blue-50" : ""}`}
                    >
                      {/* Default icon */}
                      <div
                        className="w-16 h-16 mr-3 rounded-md flex items-center justify-center flex-shrink-0"
                        style={{
                          backgroundColor: `${Constants.HOST_EXPERIENCE_BASE_COLOR}20`,
                        }}
                      >
                        <Bell
                          className="w-8 h-8"
                          style={{
                            color: Constants.HOST_EXPERIENCE_BASE_COLOR,
                          }}
                        />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start mb-1">
                          <div
                            className="font-semibold text-sm truncate flex items-center"
                            style={{
                              color: Constants.HOST_EXPERIENCE_BASE_COLOR,
                            }}
                          >
                            {notification.title}
                            {!notification.isRead && (
                              <span className="ml-2 w-2 h-2 rounded-full bg-blue-500 inline-block"></span>
                            )}
                          </div>
                          <div className="flex items-center ml-2">
                            {!notification.isRead && (
                              <button
                                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                                onClick={() => markAsRead(notification.id)}
                                title="Mark as read"
                              >
                                <CheckCircle className="w-3.5 h-3.5 text-gray-400 hover:text-green-500" />
                              </button>
                            )}
                            <button
                              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                              onClick={() => handleDeleteClick(notification)}
                              title="Delete notification"
                            >
                              <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
                            </button>
                          </div>
                        </div>

                        <div className="text-xs text-gray-600 line-clamp-2 mb-2">{notification.message}</div>

                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            <span>Received: {formatDateTime(notification.createdAt)}</span>
                          </div>
                          <div>{notification.isRead ? "Read" : "Unread"}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-sm p-6 text-center w-full">
                  <div className="text-gray-500 mb-2">No notifications found</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Confirm Delete Modal */}
      {showConfirmDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-5">
            <div className="mb-4">
              <h3 className="text-lg font-semibold">Confirm Delete</h3>
            </div>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete this notification? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <HActionButton
                label="Cancel"
                variant="outline"
                size="medium"
                onClick={() => setShowConfirmDeleteModal(false)}
              />
              <HActionButton
                label="Delete"
                variant="primary"
                color="danger"
                size="medium"
                onClick={handleConfirmDelete}
              />
            </div>
          </div>
        </div>
      )}
    </HPageContainer>
  );
};

export default HostNotifications;
